<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2019-2024 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { Button, Input, Label, RadioGroup } from '@oat-sa-private/ui-elements';
    import { Notification, PasswordInput, ModalDialog } from '@oat-sa-private/ui-components';
    import { __ } from '@oat-sa-private/ui-core';
    import LoginLayout from '@/components/Layouts/LoginLayout/LoginLayout.svelte';
    import { configurationStore } from '@/stores/tenantConfiguration.store';
    import { user } from '@/stores/auth.store';
    import { ROLE_TEST_TAKER, ROLE_BACKOFFICE } from '@/constants/roles.js';
    import { featuresStore } from '@/stores/features.store';
    import { FEATURE_ACCESS_PUBLIC_SESSIONS } from '@/constants/features';
    import ButtonLink from '@oat-sa-private/ui-elements/buttonLink/ButtonLink.svelte';

    export let error = null;
    export let tenantClientId;
    export let ssoProviders = [];
    export let displayPreferredRoleModal = false;

    let backofficeOption = __('Backoffice role');
    let testTakerOption = __('Test taker');
    let modalDialogOptions = {
        [ROLE_BACKOFFICE]: backofficeOption,
        [ROLE_TEST_TAKER]: testTakerOption
    };
    let modalDialogValue = ROLE_TEST_TAKER;

    const wasPreviouslyLoggedIn = $user !== null;

    const dispatch = createEventDispatcher();

    function handleLogin({ target }) {
        error = null;
        const formData = [...new FormData(target).entries()].reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
        dispatch('login', {
            ...formData,
            clientId: tenantClientId
        });
    }

    function handleSSOLogin(providerId) {
        dispatch('sso-login', {
            clientId: tenantClientId,
            providerId
        });
    }

    function onPreferredRoleModalAction() {
        displayPreferredRoleModal = false;
        dispatch('chosePreferredRole', {
            preferredRole: modalDialogValue
        });
    }

    const roleChoiceModalButton = [
        {
            key: 'ok',
            label: __('Confirm'),
            skin: 'primary',
            autoClose: true,
            initialFocus: true
        }
    ];
    function handlePublicTestsRedirect(event) {
        event.preventDefault();
        dispatch('anonymous-login');
    }
</script>

<style>
    :global(body) {
        height: 100%;
    }

    :global(#page) {
        height: 100%;
    }

    .wrapper {
        padding: 2.5rem 0 4rem;
    }

    .error-container {
        margin-bottom: 3rem;
    }

    :global(#page.login .actionable.primary) {
        background: var(--color-primary);
        border-color: var(--color-primary);
    }

    :global(#page.login .container) {
        max-width: 41rem;
    }

    .login-form-wrapper {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        justify-content: center;

        /* wrapper margin size varies from the smallest to the largest screens */
        margin: 4rem 0 0 0;

        & :global(button) {
            margin-left: 0;
            margin-right: 0;
        }

        & :global(button.hide-button) {
            margin-right: 0.25rem; /* to avoid overlapping input borders */
        }
    }

    .login-form :global(input) {
        height: 6rem; /* must be high enough for password show-hide button to fit */
        border: var(--border-medium) solid var(--color-border-default);
    }

    .login-form :global(.label-text) {
        line-height: 3rem;
    }

    .separator {
        display: flex;
        justify-content: center;
        border-bottom: var(--border-thin) solid var(--color-bg-info);
        height: 4.5rem;

        & span {
            background: var(--color-bg-default);
            padding: 0 1rem;
            position: relative;
            top: 3rem;
        }
    }

    .sso-providers {
        list-style: none;
        padding: 0;
        margin: 0;
        margin-block-start: 3.5rem;

        & :global(button) {
            margin-block-start: 1rem;
        }
    }

    .wrapper :global(.button-link) {
        margin-block-start: var(--space-1x);
    }
    /* overwriting ModalDialog RadioGroup to dark-UI */
    :global(.modal-wrapper .modal-positioning .dialog.inverted .radio-group.stacked .radio) {
        & :global(input:focus-visible + label) {
            @add-mixin simple-outline var(--color-brand-hover-invert);
            & :global(.radio-border) {
                stroke: var(--color-brand-hover-invert);
            }
        }

        & :global(label:hover .radio-border) {
            stroke: var(--color-brand-hover-invert);
        }

        & :global(.radio-button) {
            background-color: var(--color-bg-inverted);
        }

        & :global(.radio-check) {
            fill: var(--color-text-inverted);
        }

        & :global(.radio-border) {
            stroke: var(--color-border-inverted);
        }
    }

    @media only screen and (--mq-minwidth-medium) {
        .login-form-wrapper {
            margin: 4rem 5rem 0 5rem;
        }
    }

    @media only screen and (--mq-minwidth-huge) {
        .login-form-wrapper {
            margin: 0 13rem;
        }
    }

    @media only screen and (--mq-maxwidth-large) {
        .wrapper {
            padding: 4.5rem 0 3.5rem;
        }
    }
</style>

<LoginLayout {...$configurationStore}>
    <div class="login-form-wrapper">
        <form class="login-form" on:submit|preventDefault={handleLogin} aria-live="assertive">
            {#if wasPreviouslyLoggedIn}
                <div class="error-container">
                    <Notification
                        hierarchy="warning"
                        title={__('Login again to continue')}
                        message={__(
                            "Your session has expired and requires you to login again. You'll be back to where you were"
                        )} />
                </div>
            {/if}
            {#if error}
                <div class="error-container">
                    <Notification hierarchy="alert" title={error.title} message={error.message} />
                </div>
            {/if}
            <Label fullwidth="true" label={__('Username')}>
                <Input name="username" placeholder={__('Type here')} fullwidth="true" required feedback="inline" />
            </Label>
            <div class="wrapper">
                <Label label={__('Password')} fullwidth={true}>
                    <PasswordInput
                        name="password"
                        placeholder={__('Type here')}
                        fullwidth="true"
                        feedback="inline"
                        required />
                </Label>
                {#if $featuresStore.isFeatureEnabled(FEATURE_ACCESS_PUBLIC_SESSIONS)}
                    <ButtonLink title={__('Public sessions')} href="/public-sessions" on:click={handlePublicTestsRedirect} />
                {/if}
            </div>
            <Button size="small" type="submit" shape="pill" fullwidth="true" label={__('Log in')} />
        </form>
        {#if ssoProviders.length}
            <div class="separator">
                <span>{__('or')}</span>
            </div>
            <ul class="sso-providers">
                {#each ssoProviders as provider}
                    <li>
                        <Button
                            size="small"
                            shape="pill"
                            fullwidth="true"
                            skin="secondary"
                            label={__(provider.buttonLabel)}
                            on:click={() => handleSSOLogin(provider.id)} />
                    </li>
                {/each}
            </ul>
        {/if}
    </div>
</LoginLayout>

<ModalDialog
    name="preferredRoleModal"
    heading={__('It seems that you have been assigned multiple roles')}
    open={displayPreferredRoleModal}
    buttons={roleChoiceModalButton}
    buttonsAlignment="center"
    disableClosing
    disableEscape
    on:action={onPreferredRoleModalAction}>
    <p>{__('Would you like to log in with your Backoffice role or as a Test taker?')}</p>
    <RadioGroup name="roleChooser" options={modalDialogOptions} bind:value={modalDialogValue} />
</ModalDialog>
